export const scoreColor = (s) => s >= 72 ? "#00e676" : s >= 58 ? "#69f0ae" : s >= 44 ? "#ffd740" : s >= 30 ? "#ff9800" : "#ef5350";
export const gradeLabel = (s) => s >= 75 ? "TOP SETUP" : s >= 60 ? "ACTIVE" : s >= 45 ? "WATCH" : s >= 30 ? "WEAK" : "LOW SCORE";
export const biasColor  = (b) => b === "BULLISH" ? "#00e676" : b === "BEARISH" ? "#ef5350" : "#ffd740";
export const fmt        = (n) => (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
export const fmtCD      = (s) => { if (!s) return ""; const m = Math.floor(s / 60); return `${m}:${String(s % 60).padStart(2, "0")}`; };

/**
 * Sanitizes free-typed decimal input for a price field. A plain `<input type="number">` silently
 * empties itself if the typed value doesn't match its strict period-decimal format — many locales
 * (including Hebrew/Israeli keyboards) type a comma as the decimal separator, so that keystroke
 * alone can make a price entry quietly submit nothing, on every device, every time. Using
 * type="text" + this sanitizer instead accepts either separator.
 */
export const sanitizeDecimalInput = (raw) => {
  const cleaned = String(raw).replace(/,/g, ".").replace(/[^0-9.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
};

/**
 * Rule-based keep/sell verdict for a tracked investment.
 * stock (optional) is the latest scanned data for the symbol. The predicted direction (tomorrowBias)
 * and trend/score drive the verdict more than the raw P&L, symmetrically in both directions:
 *  - up big but the scanner just turned bearish → SELL (lock in the gain before it reverses)
 *  - down a bit but the scanner still predicts a bounce → KEEP (don't panic-sell a dip in an uptrend)
 */
export function getInvestmentAdvice(entryPrice, currentPrice, stock) {
  const pnlPct = ((currentPrice - entryPrice) / entryPrice) * 100;
  const score  = stock?.score;
  const bias   = stock?.tomorrowBias;
  const trend  = stock?.trend;

  const isBearishSignal = bias === "BEARISH" || (score != null && score < 40) || (trend != null && trend < 40);
  const isBullishSignal = bias === "BULLISH" && score != null && score >= 60;

  // Deep loss — cut it as a risk-management floor, regardless of what the scanner predicts next.
  if (pnlPct <= -15) {
    return { verdict: "SELL", pnlPct, reason: `Down ${Math.abs(pnlPct).toFixed(1)}% from your entry — consider cutting the loss.` };
  }

  if (isBearishSignal) {
    return pnlPct >= 0
      ? { verdict: "SELL", pnlPct, reason: `You're up ${pnlPct.toFixed(1)}%, but the scanner now reads bearish (score ${score ?? "—"}${bias ? `, predicted ${bias.toLowerCase()}` : ""}) — consider locking in gains before it slips further.` }
      : { verdict: "SELL", pnlPct, reason: `Down ${Math.abs(pnlPct).toFixed(1)}% and the scanner reads bearish (score ${score ?? "—"}) — the setup that got you in isn't there anymore.` };
  }

  if (isBullishSignal) {
    return pnlPct < 0
      ? { verdict: "KEEP", pnlPct, reason: `Down ${Math.abs(pnlPct).toFixed(1)}%, but the scanner still predicts bullish (score ${score}) — could be a dip in an uptrend, not a reversal.` }
      : { verdict: "KEEP", pnlPct, reason: `Up ${pnlPct.toFixed(1)}% and the scanner still predicts bullish (score ${score}) — momentum supports holding.` };
  }

  // No strong signal either way.
  if (pnlPct >= 30) {
    return { verdict: "KEEP", pnlPct, reason: `Up ${pnlPct.toFixed(1)}% with no bearish signal — consider trimming some, but nothing forcing an exit yet.` };
  }
  if (pnlPct >= 0) {
    return { verdict: "KEEP", pnlPct, reason: `Up ${pnlPct.toFixed(1)}% with no strong signal either way — keep watching.` };
  }
  return { verdict: "KEEP", pnlPct, reason: `Down ${Math.abs(pnlPct).toFixed(1)}% with no strong signal either way — not enough to flag a sell yet.` };
}
